// Function index:
//   renderAudit(host) — fetch + render the admin_audit history table with an
//       action filter, free-text search, pagination, and a Refresh button.

import { listAdminAudit, type AdminAuditEntry } from '../../api.js';
import { esc } from './helpers.js';

const ACTIONS = ['', 'role', 'verify', 'profile', 'disabled', 'reset_password', 'delete', 'bulk'];

export async function renderAudit(host: HTMLElement): Promise<void> {
  let page = 1;
  let action = '';
  let q = '';

  host.innerHTML = `
    <div class="audit-controls" style="display:flex; gap:8px; margin-bottom:10px; align-items:center; flex-wrap:wrap;">
      <input class="audit-q" placeholder="search actor/target email" style="width:240px;">
      <select class="audit-action">${ACTIONS.map((a) => `<option value="${a}">${a || 'all actions'}</option>`).join('')}</select>
      <button class="secondary audit-refresh">Refresh</button>
    </div>
    <div class="audit-body">Loading…</div>
    <div class="audit-pager" style="margin-top:10px;"></div>
  `;

  const body = host.querySelector('.audit-body') as HTMLElement;
  const pager = host.querySelector('.audit-pager') as HTMLElement;
  const qInput = host.querySelector('.audit-q') as HTMLInputElement;
  const actionSel = host.querySelector('.audit-action') as HTMLSelectElement;

  async function load(): Promise<void> {
    body.textContent = 'Loading…';
    try {
      const res = await listAdminAudit({ q: q || undefined, action: action || undefined, page, limit: 25 });
      body.innerHTML = res.entries.length ? table(res.entries) : '<div class="party-meta">no audit entries</div>';
      const pages = Math.max(1, Math.ceil(res.total / res.limit));
      pager.innerHTML = `<button class="secondary audit-prev" ${page <= 1 ? 'disabled' : ''}>‹ Prev</button>
        <span class="party-meta">page ${page} / ${pages} · ${res.total} total</span>
        <button class="secondary audit-next" ${page >= pages ? 'disabled' : ''}>Next ›</button>`;
      pager.querySelector('.audit-prev')?.addEventListener('click', () => { page--; void load(); });
      pager.querySelector('.audit-next')?.addEventListener('click', () => { page++; void load(); });
    } catch (err) {
      body.innerHTML = '';
      const e = document.createElement('div');
      e.className = 'party-meta';
      e.textContent = `Could not load audit log: ${(err as Error).message}`;
      body.appendChild(e);
    }
  }

  let t: ReturnType<typeof setTimeout> | undefined;
  qInput.addEventListener('input', () => {
    if (t) clearTimeout(t);
    t = setTimeout(() => { q = qInput.value.trim(); page = 1; void load(); }, 250);
  });
  actionSel.addEventListener('change', () => { action = actionSel.value; page = 1; void load(); });
  host.querySelector('.audit-refresh')?.addEventListener('click', () => { void load(); });

  await load();
}

function table(entries: AdminAuditEntry[]): string {
  const rows = entries.map((e) => `
    <tr>
      <td>${esc(new Date(e.at).toLocaleString())}</td>
      <td>${esc(e.actor_email ?? '—')}</td>
      <td>${esc(e.action)}</td>
      <td>${esc(e.target_email ?? '—')}</td>
      <td class="party-meta">${esc(fmtDetails(e.details))}</td>
    </tr>`).join('');
  return `<table style="width:100%; border-collapse:collapse; font-size:13px;">
    <thead><tr style="text-align:left; color:#999;"><th>when</th><th>actor</th><th>action</th><th>target</th><th>details</th></tr></thead>
    <tbody>${rows}</tbody></table>`;
}

function fmtDetails(d: Record<string, unknown>): string {
  const keys = Object.keys(d);
  if (keys.length === 0) return '';
  if ('from' in d && 'to' in d) return `${String(d.from)} → ${String(d.to)}`;
  return keys.map((k) => `${k}: ${JSON.stringify(d[k])}`).join(', ');
}
